import { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { ReactSortable } from 'react-sortablejs';

import type { ChargingStop } from '#domain/ChargingStop.ts';
import { type CapacityLimits, getTourLoadMetrics } from '#domain/loadMetrics.ts';
import type { Order } from '#domain/Order.ts';
import { keepLockedStopsAndAnchorTourEndpoints } from '#domain/stopSequence.ts';
import type { TourCapacity } from '#domain/Tour.ts';
import type { TourStop } from '#domain/TourStop.ts';
import { getRelevantCapacityMetrics } from '#domain/tourCapacity.ts';

import { ChargingStopCard } from './ChargingStopCard.tsx';
import { StopRow } from './StopRow.tsx';
import { getTourStopMapRole } from './tourStopSequence.ts';

type SortableTourListProps = {
    sortableTourStops: TourStop[];
    orders?: Order[];
    setSortableTourStops: (stops: TourStop[]) => void;
    misplacedStopIds: string[];
    onRemoveStop?: (stop: TourStop) => void;
    editableStopIds?: Set<string>;
    onEditStop?: (stop: TourStop) => void;
    showSequenceMarkers?: boolean;
    scrollable?: boolean;
    capacity?: TourCapacity;
    chargingSuggestions?: ChargingStop[];
    batteryCapacity?: number;
};

const hasSameStopOrder = (currentStops: TourStop[], nextStops: TourStop[]) =>
    currentStops.length === nextStops.length && currentStops.every((stop, index) => stop.id === nextStops[index]?.id);

const SORTABLE_GROUP = 'tour-stops';

export const SortableTourList = memo(
    ({
        sortableTourStops,
        orders,
        setSortableTourStops,
        misplacedStopIds,
        onRemoveStop,
        editableStopIds,
        onEditStop,
        showSequenceMarkers = false,
        scrollable = false,
        capacity,
        chargingSuggestions,
        batteryCapacity,
    }: SortableTourListProps) => {
        const intl = useIntl();

        const [trashItems, setTrashItems] = useState<TourStop[]>([]);
        const [isDragging, setIsDragging] = useState(false);
        const [lockedListHeight, setLockedListHeight] = useState<number | undefined>();
        const listContainerRef = useRef<HTMLDivElement | null>(null);
        const orderById = useMemo(() => new Map(orders?.map(order => [order.id, order])), [orders]);
        const capacityLimits = useMemo<CapacityLimits>(() => {
            const relevantMetrics = getRelevantCapacityMetrics(orders ?? []);
            return {
                weight: relevantMetrics.has('weight') ? capacity?.maxWeightKg : undefined,
                loadingMeter: relevantMetrics.has('loadingMeter') ? capacity?.maxLoadingMeter : undefined,
                liter: relevantMetrics.has('liter') ? capacity?.maxVolumeLiter : undefined,
                cubicMeter: relevantMetrics.has('cubicMeter') ? capacity?.maxVolumeCubicMeter : undefined,
            };
        }, [
            capacity?.maxLoadingMeter,
            capacity?.maxVolumeCubicMeter,
            capacity?.maxVolumeLiter,
            capacity?.maxWeightKg,
            orders,
        ]);
        const loadMetricsByStopId = useMemo(
            () => getTourLoadMetrics(sortableTourStops, orders ?? [], capacityLimits),
            [capacityLimits, orders, sortableTourStops]
        );

        const onUpdateStopList = useCallback(
            (newList: TourStop[]) => {
                if (newList.length < sortableTourStops.length) {
                    // Removal is handled by the trash target callback.
                    // Ignore the intermediate shorter source list and force the current rows back into view.
                    setSortableTourStops([...sortableTourStops]);
                    return;
                }

                const listWithLockedStops = keepLockedStopsAndAnchorTourEndpoints(sortableTourStops, newList, orders);
                setSortableTourStops(
                    hasSameStopOrder(sortableTourStops, listWithLockedStops) ? sortableTourStops : listWithLockedStops
                );
            },
            [orders, sortableTourStops, setSortableTourStops]
        );

        const onStopDraggedToTrash = useCallback(
            (stopsToDelete: TourStop[]) => {
                const stopToDelete = stopsToDelete[0];
                setTrashItems([]);

                if (stopToDelete && onRemoveStop) {
                    onRemoveStop(stopToDelete);
                }
            },
            [onRemoveStop]
        );

        const handleDragStart = useCallback(() => {
            setLockedListHeight(listContainerRef.current?.offsetHeight);
            setIsDragging(true);
        }, []);

        const handleDragEnd = useCallback(() => {
            setIsDragging(false);
            setLockedListHeight(undefined);
        }, []);

        return (
            <div className={scrollable ? 'height-100pct min-height-0 display-flex flex-column' : undefined}>
                <div
                    ref={listContainerRef}
                    className={scrollable ? 'min-height-0 flex-1-1 overflow-y-auto' : undefined}
                    style={lockedListHeight === undefined ? undefined : { minHeight: lockedListHeight }}
                    data-testid={scrollable ? 'tour-stops-scroll-list' : undefined}
                >
                    <ReactSortable
                        list={sortableTourStops}
                        setList={onUpdateStopList}
                        group={SORTABLE_GROUP}
                        animation={200}
                        handle='.drag-handler'
                        className={`display-grid grid-cols-1 gap-10 ${showSequenceMarkers ? '' : 'padding-right-10'}`}
                        ghostClass='opacity-0'
                        onStart={handleDragStart}
                        onEnd={handleDragEnd}
                    >
                        {sortableTourStops.map((item: TourStop, index) => (
                            <div key={item.id} className='display-grid grid-cols-1 gap-10'>
                                <StopRow
                                    item={item}
                                    order={orderById.get(item.orderId)}
                                    allStops={sortableTourStops}
                                    isMisplaced={misplacedStopIds.includes(item.id)}
                                    loadMetrics={loadMetricsByStopId.get(item.id)}
                                    canEdit={editableStopIds?.has(item.stopId ?? item.id)}
                                    onEditStop={onEditStop}
                                    onRemoveStop={onRemoveStop}
                                    sequenceMarker={
                                        showSequenceMarkers
                                            ? {
                                                  sequence: index + 1,
                                                  role: getTourStopMapRole(index, sortableTourStops.length),
                                              }
                                            : undefined
                                    }
                                />
                                {chargingSuggestions
                                    ?.filter(stop => stop.afterStopIndex === index)
                                    .map(stop => (
                                        <ChargingStopCard
                                            key={stop.id}
                                            leadingWidth={showSequenceMarkers ? 80 : 70}
                                            name={stop.name}
                                            duration={stop.duration}
                                            arrivalCharge={stop.arrivalCharge}
                                            targetCharge={stop.targetCharge}
                                            batteryCapacity={batteryCapacity ?? 0}
                                        />
                                    ))}
                            </div>
                        ))}
                    </ReactSortable>
                </div>

                {onRemoveStop && (
                    <div
                        style={{
                            maxHeight: isDragging ? 90 : 0,
                            opacity: isDragging ? 1 : 0,
                            overflow: 'hidden',
                            pointerEvents: isDragging ? 'auto' : 'none',
                            marginTop: isDragging ? 12 : 0,
                            transition: 'max-height 180ms ease, opacity 180ms ease, margin-top 180ms ease',
                        }}
                    >
                        <div className='border border-style-dashed border-color-danger position-relative rounded height-90'>
                            <ReactSortable
                                list={trashItems}
                                setList={onStopDraggedToTrash}
                                group={SORTABLE_GROUP}
                                sort={false}
                                animation={0}
                                ghostClass='opacity-0'
                                className='position-absolute inset-0'
                                style={{ minHeight: '100%' }}
                            >
                                {trashItems.map(item => (
                                    <div key={item.id} className='display-none' />
                                ))}
                            </ReactSortable>

                            <div className='position-absolute inset-0 display-grid place-items-center padding-15 pointer-events-none'>
                                <div className='display-flex gap-10 align-items-center text-color-danger'>
                                    <span className='uikit-icon icon-trash text-size-20' />
                                    <span className='text-size-16'>
                                        {intl.formatMessage({
                                            id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.remove-order',
                                        })}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }
);
